import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaMobileAlt, FaSave, FaWhatsapp, FaBell, FaPaperPlane } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

// ── Panel Notifications Push ──────────────────────────────────────────────────
const SEGMENTS = [
  // Général
  { value: 'all',             label: '👥 Tous les abonnés',                group: 'Général' },
  // Plan
  { value: 'free',            label: '🆓 Free uniquement',                 group: 'Plan' },
  { value: 'premium',         label: '⭐ Premium uniquement',              group: 'Plan' },
  // Activité
  { value: 'dormants',        label: '😴 Inactifs depuis 7j+',             group: 'Activité' },
  { value: 'actifs_7j',       label: '🔥 Actifs cette semaine',            group: 'Activité' },
  { value: 'nouveaux',        label: '🆕 Inscrits depuis 7j',              group: 'Activité' },
  { value: 'jamais_joue',     label: '😶 Jamais joué',                     group: 'Activité' },
  // Streak
  { value: 'streak_actif',    label: '🔥 En streak 3j+',                   group: 'Streak' },
  { value: 'streak_risque',   label: '⚠️ Streak à risque (joué hier)',      group: 'Streak' },
  // Mode de jeu
  { value: 'jamais_duel',     label: '⚔️ N\'a jamais fait de duel',         group: 'Mode de jeu' },
  { value: 'jamais_tournoi',  label: '🏅 N\'a jamais joué en tournoi',      group: 'Mode de jeu' },
  // Niveau
  { value: 'niveau_5plus',    label: '🌟 Niveau 5 et plus',                group: 'Niveau' },
  { value: 'niveau_10plus',   label: '👑 Niveau 10 et plus',               group: 'Niveau' },
  { value: 'peu_pieces',      label: '🪙 Moins de 100 pièces',             group: 'Niveau' },
  // Championnat
  { value: 'en_championnat',  label: '🏆 Inscrits au championnat actif',   group: 'Championnat' },
  { value: 'en_knockout',     label: '⚔️ En phase éliminatoire',            group: 'Championnat' },
];

function PushPanel() {
  const [stats, setStats]     = useState(null);
  const [title, setTitle]     = useState('');
  const [body, setBody]       = useState('');
  const [url, setUrl]         = useState('/');
  const [segment, setSegment] = useState('all');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/push/stats').then(({ data }) => setStats(data.stats)).catch(() => {});
  }, []);

  const currentCount = stats?.[segment] ?? stats?.total ?? '?';

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return toast.error('Titre et message requis');
    if (!window.confirm(`Envoyer à ce segment ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/push/broadcast', { title, body, url, segment });
      toast.success(`✅ ${data.sent} notif(s) envoyée(s)`);
      setTitle(''); setBody(''); setUrl('/');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur envoi');
    } finally { setSending(false); }
  };

  // Grouper les segments pour les optgroup
  const groups = [...new Set(SEGMENTS.map((s) => s.group))];

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border p-5 flex flex-col gap-4"
      style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)' }}>

      <div className="flex items-center gap-2">
        <FaBell className="text-qfl-gold" size={18} />
        <h2 className="font-black text-white uppercase tracking-widest text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          Notifications Push
        </h2>
        <span className="ml-auto text-xs text-white/40">
          {stats?.total ?? '—'} abonné(s) au total
        </span>
      </div>

      {/* Sélecteur de segment */}
      <div>
        <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Segment cible</label>
        <select
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
          className="form-input py-2 text-sm w-full"
          style={{ color: 'white' }}
        >
          {groups.map((group) => (
            <optgroup key={group} label={`── ${group} ──`} style={{ color: '#FFB81C' }}>
              {SEGMENTS.filter((s) => s.group === group).map((s) => (
                <option key={s.value} value={s.value} style={{ background: '#001a4d', color: 'white' }}>
                  {s.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Formulaire */}
      <div className="flex flex-col gap-3">
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre de la notification..."
          className="form-input py-2 text-sm"
          maxLength={50}
        />
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Message affiché sur le téléphone..."
          className="form-input py-2 text-sm resize-none"
          rows={3} maxLength={120}
        />
        <input
          value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="Lien à ouvrir (ex: /tournament)"
          className="form-input py-2 text-sm"
        />
      </div>

      <button onClick={handleSend} disabled={sending}
        className="flex items-center justify-center gap-2 py-3 rounded-xl font-black text-sm uppercase tracking-widest disabled:opacity-50 transition-opacity"
        style={{ background: 'linear-gradient(135deg,#FFB81C,#c97d08)', color: '#001a4d', fontFamily: "'Rajdhani', sans-serif" }}>
        <FaPaperPlane size={14} />
        {sending ? 'Envoi en cours...' : 'Envoyer la notification'}
      </button>

      <p className="text-white/25 text-xs text-center">
        Seuls les joueurs ayant autorisé les notifications recevront le message.
      </p>
    </motion.div>
  );
}

const FIELDS = [
  {
    key: 'payment_om',
    label: 'Orange Money',
    placeholder: 'Ex : +225 07 00 00 00 00',
    logo: '/logo-om.png',
    bg: '#ff6600',
  },
  {
    key: 'payment_mtn',
    label: 'MTN Mobile Money',
    placeholder: 'Ex : +225 05 00 00 00 00',
    logo: '/logo-mtn.jpg',
    bg: '#ffcc00',
  },
  {
    key: 'payment_wave',
    label: 'Wave',
    placeholder: 'Ex : +225 01 00 00 00 00',
    logo: '/logo-wave.png',
    bg: '#1a73e8',
  },
  {
    key: 'payment_contact',
    label: 'WhatsApp de confirmation',
    placeholder: 'Ex : +225 07 00 00 00 00 (sans espaces pour lien WA)',
    logo: null,
    bg: '#25d366',
    icon: <FaWhatsapp size={12} color="#fff" />,
  },
];

function AdminSettings() {
  const [form, setForm]     = useState({
    payment_om: '',
    payment_mtn: '',
    payment_wave: '',
    payment_contact: '',
    payment_instructions: '',
  });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    api.get('/admin/settings/payment')
      .then(({ data }) => {
        if (data.settings) setForm((f) => ({ ...f, ...data.settings }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/payment', form);
      toast.success('Paramètres de paiement mis à jour');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1
          className="text-2xl font-black text-white uppercase tracking-widest"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          Paramètres
        </h1>
        <p className="text-white/40 text-sm">Numéros Mobile Money affichés aux utilisateurs lors du paiement</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="card flex flex-col gap-5"
        >
          <div className="flex items-center gap-2 mb-1">
            <FaMobileAlt className="text-qfl-gold" />
            <span
              className="text-white font-bold text-sm uppercase tracking-widest"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              Numéros de paiement
            </span>
          </div>

          {FIELDS.map((field) => (
            <div key={field.key}>
              <label className="flex items-center gap-2 text-white/60 text-xs mb-1.5">
                <span
                  className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden bg-white"
                  style={{ background: field.logo ? '#fff' : field.bg }}
                >
                  {field.logo
                    ? <img src={field.logo} alt={field.label} className="w-full h-full object-contain" />
                    : field.icon}
                </span>
                {field.label}
              </label>
              <input
                type="text"
                value={form[field.key]}
                onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                placeholder={field.placeholder}
                className="form-input w-full text-sm py-2"
              />
            </div>
          ))}

          {/* Instructions personnalisées */}
          <div>
            <label className="text-white/60 text-xs mb-1.5 block">
              Instructions affichées à l'utilisateur
            </label>
            <textarea
              value={form.payment_instructions}
              onChange={(e) => setForm((f) => ({ ...f, payment_instructions: e.target.value }))}
              rows={3}
              placeholder="Ex : Envoyez le montant exact, puis contactez-nous avec votre pseudo et la capture d'écran."
              className="form-input w-full text-sm py-2 resize-none"
            />
          </div>

          {/* Aperçu */}
          <div
            className="rounded-xl p-3 text-xs text-white/40"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <p className="mb-1 text-white/60 font-bold uppercase tracking-wider text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Aperçu utilisateur
            </p>
            {FIELDS.slice(0, 3).map((f) =>
              form[f.key] ? (
                <p key={f.key} className="flex items-center gap-2 mt-1">
                  <span className="w-5 h-5 rounded overflow-hidden bg-white flex-shrink-0 flex items-center justify-center">
                    <img src={f.logo} alt={f.label} className="w-full h-full object-contain" />
                  </span>
                  <span className="text-white/70">{form[f.key]}</span>
                </p>
              ) : null
            )}
            {!FIELDS.slice(0, 3).some((f) => form[f.key]) && (
              <p className="text-white/30 italic">Aucun numéro configuré — le modal affichera un message d'attente</p>
            )}
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 py-3 rounded-xl font-black text-sm uppercase tracking-widest transition-opacity disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, #FFB81C, #c97d08)',
              color: '#001a4d',
              fontFamily: "'Rajdhani', sans-serif",
            }}
          >
            <FaSave size={14} />
            {saving ? 'Enregistrement...' : 'ENREGISTRER'}
          </button>
        </motion.div>
      )}
      {/* Panel notifications push */}
      <PushPanel />
    </div>
  );
}

export default AdminSettings;

